export interface MortgageResults {
  monthlyPayment: number;
  biWeeklyPayment: number;
  weeklyPayment: number;
  totalPayment: number;
  totalInterest: number;
  principalAmount: number;
  cmhcInsurance: number;
  monthlyPropertyTax: number;
  totalMonthlyPayment: number;
  stressTestPayment: number;
  landTransferTax: number;
}

/**
 * Calculate CMHC insurance premium based on LTV ratio (Canadian rules)
 */
export const calculateCMHCInsurance = (homePrice: number, downPayment: number): number => {
  const loanAmount = homePrice - downPayment;
  const ltv = (loanAmount / homePrice) * 100;

  // No CMHC needed if down payment is >= 20%
  if (ltv <= 80) return 0;

  let premiumRate = 0;
  if (ltv > 95) premiumRate = 0.04; // 4.00%
  else if (ltv > 90) premiumRate = 0.031; // 3.10%
  else if (ltv > 85) premiumRate = 0.028; // 2.80%
  else if (ltv > 80) premiumRate = 0.024; // 2.40%

  return loanAmount * premiumRate;
};

/**
 * Calculate land transfer tax for Ontario (can be extended for other provinces)
 */
export const calculateLandTransferTax = (homePrice: number, isFirstTimeBuyer: boolean = false): number => {
  let tax = 0;

  // Ontario tiered rates
  if (homePrice <= 55000) {
    tax = homePrice * 0.005;
  } else if (homePrice <= 250000) {
    tax = 275 + (homePrice - 55000) * 0.01;
  } else if (homePrice <= 400000) {
    tax = 2225 + (homePrice - 250000) * 0.015;
  } else if (homePrice <= 2000000) {
    tax = 4475 + (homePrice - 400000) * 0.02;
  } else {
    tax = 36475 + (homePrice - 2000000) * 0.025;
  }

  // First-time homebuyer rebate (up to $4,000)
  if (isFirstTimeBuyer) {
    const rebate = Math.min(4000, tax);
    tax -= rebate;
  }

  return tax;
};

/**
 * Calculate mortgage payment based on frequency
 */
export const calculateMortgagePayment = (
  principal: number,
  annualRate: number,
  amortizationYears: number,
  frequency: 'monthly' | 'biweekly' | 'accelerated-biweekly' | 'weekly'
): number => {
  const monthlyRate = annualRate / 100 / 12;
  const numberOfPayments = amortizationYears * 12;

  // Calculate monthly payment first
  const monthlyPayment =
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

  switch (frequency) {
    case 'monthly':
      return monthlyPayment;
    case 'biweekly':
      return (monthlyPayment * 12) / 26;
    case 'accelerated-biweekly':
      return monthlyPayment / 2;
    case 'weekly':
      return (monthlyPayment * 12) / 52;
    default:
      return monthlyPayment;
  }
};

/**
 * Calculate full mortgage results
 */
export const calculateMortgage = (
  homePrice: number,
  downPayment: number,
  interestRate: number,
  amortization: number,
  propertyTax: number = 0,
  condoFees: number = 0,
  heatingCosts: number = 0,
  isFirstTimeBuyer: boolean = false
): MortgageResults => {
  const cmhcInsurance = calculateCMHCInsurance(homePrice, downPayment);
  const principalAmount = homePrice - downPayment + cmhcInsurance;

  const monthlyPayment = calculateMortgagePayment(principalAmount, interestRate, amortization, 'monthly');
  const biWeeklyPayment = calculateMortgagePayment(principalAmount, interestRate, amortization, 'biweekly');
  const weeklyPayment = calculateMortgagePayment(principalAmount, interestRate, amortization, 'weekly');

  const totalPayment = monthlyPayment * amortization * 12;
  const totalInterest = totalPayment - principalAmount;

  const monthlyPropertyTax = propertyTax / 12;
  const monthlyHeatingCosts = heatingCosts / 12;
  const totalMonthlyPayment = monthlyPayment + monthlyPropertyTax + condoFees + monthlyHeatingCosts;

  // Stress test at 5.25% or contract rate + 2%, whichever is higher
  const stressTestRate = Math.max(5.25, interestRate + 2);
  const stressTestPayment = calculateMortgagePayment(principalAmount, stressTestRate, amortization, 'monthly');

  const landTransferTax = calculateLandTransferTax(homePrice, isFirstTimeBuyer);

  return {
    monthlyPayment,
    biWeeklyPayment,
    weeklyPayment,
    totalPayment,
    totalInterest,
    principalAmount,
    cmhcInsurance,
    monthlyPropertyTax,
    totalMonthlyPayment,
    stressTestPayment,
    landTransferTax,
  };
};

/**
 * Generate amortization schedule
 */
export interface AmortizationScheduleItem {
  year: number;
  principal: number;
  interest: number;
  balance: number;
}

export const generateAmortizationSchedule = (
  principal: number,
  annualRate: number,
  amortizationYears: number
): AmortizationScheduleItem[] => {
  const schedule: AmortizationScheduleItem[] = [];
  const monthlyRate = annualRate / 100 / 12;
  const monthlyPayment = calculateMortgagePayment(principal, annualRate, amortizationYears, 'monthly');

  let balance = principal;

  for (let year = 1; year <= amortizationYears; year++) {
    let yearlyPrincipal = 0;
    let yearlyInterest = 0;

    for (let month = 1; month <= 12; month++) {
      if (balance <= 0) break;

      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;

      yearlyPrincipal += principalPayment;
      yearlyInterest += interestPayment;
      balance -= principalPayment;
    }

    schedule.push({
      year,
      principal: yearlyPrincipal,
      interest: yearlyInterest,
      balance: Math.max(0, balance),
    });
  }

  return schedule;
};
